import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SortingState } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Pagination } from '@/common/pagination';
import { toSortParam } from '@/components/data-table/sort-param';
import { type DataUploadsListQueryFilterType, getListDataUploads } from '@/queries/data-uploads/list-data-uploads';
import { ROLE } from '../../../common/role';
import { useAppContext } from '../../../context/AppContext';
import { AbsoluteLoader } from '../../absoluteLoader';
import { Restricted } from '../../Restricted';
import { Add3DDataDialog } from './Add3DDataDialog';
import { DATA_UPLOAD_SORT_FIELDS } from './columns';
import { DataUploadList } from './DataUploadList';
import { UploadFiles } from './upload';

export type DataUploadListContainerProps = {
	assetId: string;
	type: 'asset' | 'inspection' | 'damage';
	inspectionId?: string;
	damageId?: string;
};
export function DataUploadListContainer({ assetId, inspectionId, damageId, type }: DataUploadListContainerProps) {
	const { t } = useTranslation();
	const { selectedAssetId } = useAppContext();
	const queryClient = useQueryClient();
	const [filters, setFilters] = useState({
		assetId,
		inspectionId,
		damageId,
		fileType: '',
		subAssetId: (assetId && selectedAssetId && assetId !== selectedAssetId && selectedAssetId) || '',
	} as DataUploadsListQueryFilterType);
	useEffect(() => {
		setFilters((prev) => ({
			...prev,
			subAssetId: (assetId && selectedAssetId && assetId !== selectedAssetId && selectedAssetId) || '',
		}));
	}, [selectedAssetId, assetId]);
	const [sorting, setSorting] = useState<SortingState>([]);
	// "Show deleted" (GH #331): only deleted files of this level, per page visit
	const [showDeleted, setShowDeleted] = useState(false);
	const sort = toSortParam(sorting, DATA_UPLOAD_SORT_FIELDS);
	const [pagination, _setPagination] = useState({
		limit: 100,
		offset: 0,
	} as Pagination);
	const filteredFilters = useMemo(
		() =>
			Object.fromEntries(
				Object.entries(filters).filter(([_, value]) => value !== '' && value !== undefined),
			) as DataUploadsListQueryFilterType,
		[filters],
	);
	const {
		data: dataUploadsResponse,
		isLoading,
		isRefetching,
		refetch,
	} = useQuery({
		queryKey: ['data-uploads', 'list', filteredFilters, pagination, sort, showDeleted],
		queryFn: () =>
			getListDataUploads({ ...filteredFilters, ...pagination, sort, ...(showDeleted && { deleted: true }) }),
		enabled: !!assetId,
		// Keep the current rows on screen while a re-sort or re-filter loads.
		placeholderData: keepPreviousData,
		// While any listed tileset is still queued/processing, keep refetching so the
		// status badge follows the backend sweeper's updates without a manual refresh.
		// A deleted upload's conversion cannot be running (it refuses the delete), so the deleted view never polls.
		refetchInterval: (query) =>
			!showDeleted &&
			query.state.data?.dataUploads?.some(
				(upload) => upload.tileset?.status === 'queued' || upload.tileset?.status === 'processing',
			)
				? 15 * 1000
				: false,
	});
	// Filter the backend response to strictly match the view level
	const dataUploads = (dataUploadsResponse?.dataUploads || []).filter((upload) => {
		if (type === 'asset') {
			// Asset level should not have an inspectionId or damageId
			return !upload.inspectionId && !upload.damageId;
		}
		if (type === 'inspection') {
			// Inspection level should have an inspectionId, but no damageId
			return upload.inspectionId === inspectionId && !upload.damageId;
		}
		if (type === 'damage') {
			// Damage level must match the damageId
			return upload.damageId === damageId;
		}
		return true;
	});

	const onRefresh = () => {
		refetch();

		if (type === 'damage') {
			queryClient.invalidateQueries({ queryKey: ['damage-polygons'] });
		}
	};
	const onFilterChnage = (newFilters: DataUploadsListQueryFilterType) => {
		setFilters((prev) => ({
			...prev,
			...newFilters,
		}));
	};
	if (type === 'asset' && !assetId) {
		return <div className="p-6">{t('files.list.noAssetSelected')}</div>;
	}
	if (type === 'inspection' && !inspectionId) {
		return <div className="p-6">{t('files.list.noInspectionSelected')}</div>;
	}
	if (type === 'damage' && !damageId) {
		return <div className="p-6">{t('files.list.noDamageSelected')}</div>;
	}
	return (
		<div className="flex-1">
			<div className="space-y-4">
				<div className="flex justify-between items-center">
					<h3 className="font-medium text-sm lg:text-base">{t('files.list.heading')}</h3>
					<Restricted to={ROLE.DATA_UPLOADS_CREATE}>
						<div className="flex gap-1.5">
							{type === 'asset' && (
								<Add3DDataDialog onAdd={onRefresh} inspectionId={inspectionId} damageId={damageId} assetId={assetId} />
							)}
							<UploadFiles onAdd={onRefresh} inspectionId={inspectionId} damageId={damageId} assetId={assetId} />
						</div>
					</Restricted>
				</div>
				<div className="relative">
					{<AbsoluteLoader isLoading={isLoading || isRefetching} />}
					<DataUploadList
						assetId={assetId}
						dataUploads={dataUploads}
						filters={filters}
						onFilterChange={onFilterChnage}
						doRefresh={onRefresh}
						sorting={sorting}
						onSortingChange={setSorting}
						showDeleted={showDeleted}
						onShowDeletedChange={setShowDeleted}
					/>
				</div>
			</div>
		</div>
	);
}
